// Case Buddy domain module generated from CaseBuddyCore.js.
// Spreadsheet-shaped rows remain the source of truth.
import { normalizeCell, rowHasMeaningfulData } from './CaseBuddyShared.js';
import { rowLooksLikeCaseBuddyHeader } from './CaseBuddySheetData.js';
import { normalizeCaseMatrix } from './CaseBuddyWorkbookState.js';

export function normalizeCaseBuddyAttributionAddress(value) {
  const text = normalizeCell(value);
  if (!text) return '';

  if (/^0x/i.test(text)) return text.toLowerCase();

  const lower = text.toLowerCase();
  if (lower.startsWith('bc1') || lower.startsWith('tb1') || lower.startsWith('ltc1') || lower.startsWith('bcrt1')) {
    return lower;
  }

  return text;
}

export function normalizeAttributionValue(value) {
  return normalizeCell(value).replace(/\s+/g, ' ');
}

export function normalizeAttributionHeaderCell(value) {
  return normalizeCell(value).replace(/[\s_\-./]/g, '').toLowerCase();
}

export function isAddressAttributionAddressHeader(value) {
  const normalized = normalizeAttributionHeaderCell(value);
  return ['address', 'fromaddress', 'toaddress', 'destinationaddress', 'wallet', 'walletaddress', 'account', 'accountaddress'].includes(normalized);
}

export function isAddressAttributionLabelHeader(value) {
  const normalized = normalizeAttributionHeaderCell(value);
  return ['attribution', 'attributiondata', 'label', 'entity', 'service', 'name', 'source', 'owner'].includes(normalized);
}

export function looksLikeAttributionAddress(value, knownKeys = new Set()) {
  const text = normalizeCell(value);
  if (!text) return false;
  const key = normalizeCaseBuddyAttributionAddress(text);
  if (knownKeys.has(key)) return true;
  if (/^0x[0-9a-f]{16,}$/i.test(text)) return true;
  if (/^(bc1|tb1|ltc1|bcrt1)[a-z0-9]{10,}$/i.test(text)) return true;
  if (/^[13][a-km-zA-HJ-NP-Z1-9]{24,40}$/.test(text)) return true;
  return text.length >= 16 && /^[a-zA-Z0-9:_\-.]+$/.test(text) && /\d/.test(text);
}

export function parseAddressAttributionTsv(text = '', knownTargets = []) {
  const rows = String(text || '')
    .replace(/\r\n/g, '\n')
    .replace(/\r/g, '\n')
    .split('\n')
    .map(line => line.split('\t').map(cell => normalizeCell(cell)))
    .filter(row => row.some(cell => cell));

  const knownKeys = new Set((Array.isArray(knownTargets) ? knownTargets : []).map(target => target.key).filter(Boolean));
  const mappings = [];
  const seen = new Set();
  let duplicateCount = 0;
  let skippedCount = 0;
  let addressColumn = -1;
  let attributionColumn = -1;
  let startIndex = 0;

  if (rows.length > 0) {
    rows[0].forEach((cell, index) => {
      if (addressColumn < 0 && isAddressAttributionAddressHeader(cell)) addressColumn = index;
      if (attributionColumn < 0 && isAddressAttributionLabelHeader(cell)) attributionColumn = index;
    });
    if (addressColumn >= 0 && attributionColumn >= 0) startIndex = 1;
  }

  for (let rowIndex = startIndex; rowIndex < rows.length; rowIndex += 1) {
    const row = rows[rowIndex] || [];
    if (!row.some(cell => cell)) continue;

    let address = '';
    let attribution = '';

    if (addressColumn >= 0 && attributionColumn >= 0) {
      address = row[addressColumn] || '';
      attribution = row[attributionColumn] || '';
    } else if (row.length >= 2) {
      const first = row[0] || '';
      const second = row[1] || '';
      const firstKey = normalizeCaseBuddyAttributionAddress(first);
      const secondKey = normalizeCaseBuddyAttributionAddress(second);
      const firstLooksAddress = knownKeys.has(firstKey) || looksLikeAttributionAddress(first, knownKeys);
      const secondLooksAddress = knownKeys.has(secondKey) || looksLikeAttributionAddress(second, knownKeys);

      if (secondLooksAddress && (!firstLooksAddress || knownKeys.has(secondKey))) {
        address = second;
        attribution = first;
      } else {
        address = first;
        attribution = second;
      }
    } else if (row.length === 1) {
      address = row[0] || '';
      attribution = '';
    }

    if (isAddressAttributionAddressHeader(address) || isAddressAttributionLabelHeader(address)) continue;

    const key = normalizeCaseBuddyAttributionAddress(address);
    if (!key || !address) {
      skippedCount += 1;
      continue;
    }

    if (seen.has(key)) duplicateCount += 1;
    seen.add(key);

    mappings.push({
      rowNumber: rowIndex + 1,
      address: normalizeCell(address),
      key,
      attribution: normalizeAttributionValue(attribution),
      matchedKnownTarget: knownKeys.has(key)
    });
  }

  return {
    rows,
    mappings,
    parsedRowCount: rows.length,
    distinctMappingCount: new Set(mappings.map(mapping => mapping.key)).size,
    duplicateCount,
    skippedCount,
    hasMappings: mappings.length > 0
  };
}

export function extractStructuredAttributionPart(value, side) {
  const text = normalizeAttributionValue(value);
  if (!text) return '';
  const pattern = side === 'from'
    ? /(?:^|[|;])\s*from\s*:\s*([^|;]+)/i
    : /(?:^|[|;])\s*to\s*:\s*([^|;]+)/i;
  const match = text.match(pattern);
  return match ? normalizeAttributionValue(match[1]) : '';
}

export function attributionLooksStructured(value) {
  return /(?:^|[|;])\s*(from|to)\s*:/i.test(normalizeAttributionValue(value));
}

export function readAddressOccurrenceAttribution(row, side) {
  const attribution = normalizeAttributionValue(row?.[10]);
  if (!attribution) return '';
  if (!attributionLooksStructured(attribution)) return attribution;
  return extractStructuredAttributionPart(attribution, side) || attribution;
}

export function formatAddressAttributionSideSummary(target = {}) {
  const parts = [];
  if (target.fromCount) parts.push(`${target.fromCount} From`);
  if (target.toCount) parts.push(`${target.toCount} To`);
  return parts.join(' · ') || 'No row refs';
}

export function pushUniqueAttributionValue(values, value) {
  const text = normalizeAttributionValue(value);
  if (!text) return;
  if (!values.some(existing => existing.toLowerCase() === text.toLowerCase())) values.push(text);
}

export function buildAddressAttributionAnalysis(mainData = []) {
  const data = normalizeCaseMatrix(mainData);
  const targetsByKey = new Map();
  let rowOccurrenceCount = 0;
  let missingRowCount = 0;
  let addressOccurrenceCount = 0;
  let missingOccurrenceCount = 0;

  data.forEach((row, rowIndex) => {
    if (rowIndex === 0 || !rowHasMeaningfulData(row) || rowLooksLikeCaseBuddyHeader(row)) return;

    const fromAddress = normalizeCell(row?.[2]);
    const toAddress = normalizeCell(row?.[3]);
    const rowHasAddress = !!(fromAddress || toAddress);
    const rowAttribution = normalizeAttributionValue(row?.[10]);

    if (rowHasAddress) {
      rowOccurrenceCount += 1;
      if (!rowAttribution) missingRowCount += 1;
    }

    [
      { side: 'from', label: 'From', address: fromAddress },
      { side: 'to', label: 'To', address: toAddress }
    ].forEach(({ side, label, address }) => {
      if (!address || isAddressAttributionAddressHeader(address)) return;
      const key = normalizeCaseBuddyAttributionAddress(address);
      if (!key) return;

      let target = targetsByKey.get(key);
      if (!target) {
        target = {
          id: `address-attribution-${targetsByKey.size + 1}-${key}`,
          key,
          address,
          firstRowIndex: rowIndex,
          firstSpreadsheetRow: rowIndex + 1,
          fromCount: 0,
          toCount: 0,
          occurrences: [],
          attributionValues: []
        };
        targetsByKey.set(key, target);
      }

      if (rowIndex < target.firstRowIndex) {
        target.firstRowIndex = rowIndex;
        target.firstSpreadsheetRow = rowIndex + 1;
        target.address = address;
      }

      const occurrenceAttribution = readAddressOccurrenceAttribution(row, side);
      if (occurrenceAttribution) pushUniqueAttributionValue(target.attributionValues, occurrenceAttribution);
      if (side === 'from') target.fromCount += 1;
      if (side === 'to') target.toCount += 1;

      const occurrence = {
        rowIndex,
        spreadsheetRow: rowIndex + 1,
        side,
        sideLabel: label,
        address,
        key,
        currentAttribution: occurrenceAttribution,
        rowAttribution
      };

      target.occurrences.push(occurrence);
      addressOccurrenceCount += 1;
      if (!occurrenceAttribution) missingOccurrenceCount += 1;
    });
  });

  const targets = Array.from(targetsByKey.values()).map((target) => {
    const currentAttribution = target.attributionValues.length === 0
      ? ''
      : target.attributionValues.length === 1
        ? target.attributionValues[0]
        : target.attributionValues.join(' / ');
    const missingOccurrences = target.occurrences.filter(occurrence => !occurrence.currentAttribution);
    const complete = target.occurrences.length > 0 && missingOccurrences.length === 0;
    const unknown = target.attributionValues.some(value => value.toLowerCase() === 'unknown');

    return {
      ...target,
      currentAttribution,
      complete,
      unknown,
      missingOccurrenceCount: missingOccurrences.length,
      missingOccurrences,
      sideSummary: formatAddressAttributionSideSummary(target)
    };
  }).sort((left, right) => {
    if (left.complete !== right.complete) return left.complete ? 1 : -1;
    if (left.firstRowIndex !== right.firstRowIndex) return left.firstRowIndex - right.firstRowIndex;
    return String(left.address || '').localeCompare(String(right.address || ''));
  });

  const missingTargets = targets.filter(target => !target.complete);
  const completeTargets = targets.filter(target => target.complete);
  const unknownTargets = targets.filter(target => target.unknown);

  return {
    targets,
    missingTargets,
    completeTargets,
    unknownTargets,
    addressCount: targets.length,
    uniqueAddressCount: targets.length,
    missingCount: missingTargets.length,
    completeCount: completeTargets.length,
    unknownCount: unknownTargets.length,
    occurrenceCount: addressOccurrenceCount,
    missingOccurrenceCount,
    rowOccurrenceCount,
    missingRowCount,
    ready: targets.length > 0 && missingOccurrenceCount === 0,
    hasAddresses: targets.length > 0,
    summary: targets.length === 0
      ? 'No From or To addresses were found in Main Data.'
      : missingOccurrenceCount === 0
        ? `All ${targets.length} unique From/To address${targets.length === 1 ? '' : 'es'} have attribution or Unknown wherever they appear.`
        : `${missingTargets.length} unique address${missingTargets.length === 1 ? '' : 'es'} and ${missingOccurrenceCount} address occurrence${missingOccurrenceCount === 1 ? '' : 's'} still need attribution or Unknown.`
  };
}

export function combineRowAttributionForAddressMappings(row, fromValue, toValue, overwriteExisting = false) {
  const existing = normalizeAttributionValue(row?.[10]);
  const from = normalizeAttributionValue(fromValue);
  const to = normalizeAttributionValue(toValue);
  const hasFrom = !!normalizeCell(row?.[2]);
  const hasTo = !!normalizeCell(row?.[3]);

  if (!from && !to) return existing;
  if (!overwriteExisting && existing) return existing;

  if (hasFrom && hasTo && from && to && from.toLowerCase() !== to.toLowerCase()) {
    return `From: ${from} | To: ${to}`;
  }

  if (attributionLooksStructured(existing)) {
    const currentFrom = from || extractStructuredAttributionPart(existing, 'from');
    const currentTo = to || extractStructuredAttributionPart(existing, 'to');
    const parts = [];
    if (currentFrom) parts.push(`From: ${currentFrom}`);
    if (currentTo) parts.push(`To: ${currentTo}`);
    return parts.join(' | ') || existing;
  }

  if (from && to && from.toLowerCase() === to.toLowerCase()) return from;
  if (to) return to;
  if (from) return from;
  return existing;
}

export function applyAddressAttributionMappingsToMainMatrix(mainData = [], mappings = [], options = {}) {
  const data = normalizeCaseMatrix(mainData);
  const overwriteExisting = !!options.overwriteExisting;
  const map = new Map();
  let duplicateInputAddressCount = 0;

  (Array.isArray(mappings) ? mappings : []).forEach((mapping) => {
    const key = normalizeCaseBuddyAttributionAddress(mapping?.address || mapping?.key || '');
    const attribution = normalizeAttributionValue(mapping?.attribution || mapping?.value || mapping?.label || '');
    if (!key || !attribution) return;
    if (map.has(key)) duplicateInputAddressCount += 1;
    map.set(key, attribution);
  });

  let matchedRowCount = 0;
  let matchedOccurrenceCount = 0;
  let updatedRowCount = 0;
  const updatedRows = [];
  const touchedAddressKeys = new Set();

  for (let rowIndex = 1; rowIndex < data.length; rowIndex += 1) {
    const row = data[rowIndex];
    if (!rowHasMeaningfulData(row) || rowLooksLikeCaseBuddyHeader(row)) continue;

    const fromKey = normalizeCaseBuddyAttributionAddress(row?.[2]);
    const toKey = normalizeCaseBuddyAttributionAddress(row?.[3]);
    const fromValue = fromKey && map.has(fromKey) ? map.get(fromKey) : '';
    const toValue = toKey && map.has(toKey) ? map.get(toKey) : '';

    if (!fromValue && !toValue) continue;
    matchedRowCount += 1;
    if (fromValue) {
      matchedOccurrenceCount += 1;
      touchedAddressKeys.add(fromKey);
    }
    if (toValue) {
      matchedOccurrenceCount += 1;
      touchedAddressKeys.add(toKey);
    }

    const previousAttribution = normalizeAttributionValue(row?.[10]);
    const nextAttribution = combineRowAttributionForAddressMappings(row, fromValue, toValue, overwriteExisting);
    if (nextAttribution && nextAttribution !== previousAttribution) {
      row[10] = nextAttribution;
      updatedRowCount += 1;
      updatedRows.push({
        rowIndex,
        spreadsheetRow: rowIndex + 1,
        fromAddress: normalizeCell(row?.[2]),
        toAddress: normalizeCell(row?.[3]),
        previousAttribution,
        newAttribution: nextAttribution
      });
    }
  }

  const analysisAfter = buildAddressAttributionAnalysis(data);

  return {
    data,
    changed: updatedRowCount > 0,
    distinctInputMappings: map.size,
    duplicateInputAddressCount,
    matchedRowCount,
    matchedOccurrenceCount,
    updatedRowCount,
    updatedRows,
    touchedAddressCount: touchedAddressKeys.size,
    analysisAfter,
    remainingMissingCount: analysisAfter.missingCount,
    remainingMissingOccurrenceCount: analysisAfter.missingOccurrenceCount,
    completedAddressCountAfterApply: analysisAfter.completeCount,
    totalAddressCountAfterApply: analysisAfter.addressCount
  };
}

export function buildAddressAttributionAuditRows(analysisBefore = {}, applyResult = {}, analysisAfter = null) {
  const after = analysisAfter || applyResult.analysisAfter || {};
  const updatedRows = Array.isArray(applyResult.updatedRows) ? applyResult.updatedRows : [];
  return [
    ['Case Buddy Address Attribution Audit', '', '', '', '', '', '', '', '', 'Generated by Fill address attribution in main data. Audit only; do not append this back into Main Data.'],
    ['Unique Addresses Before', analysisBefore.addressCount || 0, 'Missing Before', analysisBefore.missingCount || 0, 'Missing Occurrences Before', analysisBefore.missingOccurrenceCount || 0, 'Mappings Applied', applyResult.distinctInputMappings || 0],
    ['Unique Addresses After', after.addressCount || 0, 'Missing After', after.missingCount || 0, 'Missing Occurrences After', after.missingOccurrenceCount || 0, 'Rows Updated', applyResult.updatedRowCount || 0],
    ['Main Row', 'From Address', 'To Address', 'Previous Column K', 'New Column K'],
    ...updatedRows.map(row => [
      row.spreadsheetRow || '',
      row.fromAddress || '',
      row.toAddress || '',
      row.previousAttribution || '',
      row.newAttribution || ''
    ])
  ];
}

export function buildAddressAttributionExportTsvFromTargets(targets = []) {
  return [
    ['Address', 'Attribution'],
    ...(Array.isArray(targets) ? targets : []).map(target => [target.address || '', target.currentAttribution || ''])
  ].map(row => row.map(cell => {
    const text = cell == null ? '' : String(cell);
    return /["\n\t]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
  }).join('\t')).join('\n');
}
